import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import Panel from './Panel';
import MediaSelect from './MediaSelect';
import CreateMediaOutletModal from './MediaSelect/CreateMediaOutletModal';

function MediaAndDistribution({
  backlinkOpportunity,
  distribution,
  distributeExternally,
  eventType,
  lookingFor,
  paid,
  speakerRequest,
  mediaOutletId,
  toggleParam,
  updateParam,
  useUpdateParamWithEvent,
  validationErrors,
}) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [initialOutletName, setInitialOutletName] = useState('');
  const handleDistributionChange = useCallback(
    (value) => () => updateParam('distribution', distribution === value ? null : value),
    [updateParam, distribution],
  );
  const handleEventTypeChange = useCallback(
    (value) => () => updateParam('eventType', eventType === value ? null : value),
    [updateParam, eventType],
  );
  const mediaOnChange = useCallback((value) => updateParam('mediaOutletId', String(value || '')), [updateParam]);
  const showSpeakerCheckbox = distribution === 'event' && lookingFor === 'person';
  const showEventType = speakerRequest === true;

  const handleCreateOutlet = useCallback(
    (newOutlet) => {
      // Auto-select the newly created outlet
      updateParam('mediaOutletId', String(newOutlet.id || ''));
    },
    [updateParam],
  );

  const handleCreateModalToggle = useCallback((searchText = '') => {
    setInitialOutletName(searchText);
    setShowCreateModal((prev) => !prev);
  }, []);

  return (
    <div>
      <Panel>
        <div className="mb-4">
          <p className="mb-2" style={{ fontSize: '16px' }}>
            <b>{speakerRequest ? 'Where will this speaker appear?' : 'Where will this appear?'}</b>
          </p>
          <MediaSelect
            value={mediaOutletId}
            hasError={!!validationErrors.mediaOutletId}
            onChange={mediaOnChange}
            onCreateClick={handleCreateModalToggle}
          />
          {validationErrors.mediaOutletId && (
            <div className="invalid-feedback d-block mt-1">{validationErrors.mediaOutletId}</div>
          )}
          <CreateMediaOutletModal
            isOpen={showCreateModal}
            toggle={handleCreateModalToggle}
            onCreateSuccess={handleCreateOutlet}
            initialName={initialOutletName}
          />
        </div>

        <div className="mb-4">
          <p className="mb-2">
            <b>Distribution</b>
          </p>
          <div className="d-flex flex-shrink-0 flex-wrap gap-2">
            <input
              id="distributionOnline"
              type="radio"
              name="distribution"
              className="btn-check"
              onChange={handleDistributionChange('online')}
              checked={distribution === 'online'}
            />
            <label
              htmlFor="distributionOnline"
              name="distribution"
              className={distribution === 'online' ? 'btn btn-sm btn-primary' : 'btn btn-sm new-request-option-btn'}
            >
              <i className="fa fa-newspaper me-2" />
              Online/Print
            </label>
            <input
              id="distributionRadio"
              type="radio"
              name="distribution"
              className="btn-check"
              onChange={handleDistributionChange('radio')}
              checked={distribution === 'radio'}
            />
            <label
              htmlFor="distributionRadio"
              name="distribution"
              className={distribution === 'radio' ? 'btn btn-sm btn-primary' : 'btn btn-sm new-request-option-btn'}
            >
              <i className="fa fa-microphone me-2" />
              Radio
            </label>
            <input
              id="distributionPodcast"
              type="radio"
              name="distribution"
              className="btn-check"
              onChange={handleDistributionChange('podcast')}
              checked={distribution === 'podcast'}
            />
            <label
              htmlFor="distributionPodcast"
              name="distribution"
              className={distribution === 'podcast' ? 'btn btn-sm btn-primary' : 'btn btn-sm new-request-option-btn'}
            >
              <i className="fa fa-microphone-lines me-2" />
              Podcast
            </label>
            <input
              id="distributionVideo"
              type="radio"
              name="distribution"
              className="btn-check"
              onChange={handleDistributionChange('video')}
              checked={distribution === 'video'}
            />
            <label
              htmlFor="distributionVideo"
              className={distribution === 'video' ? 'btn btn-sm btn-primary' : 'btn btn-sm new-request-option-btn'}
            >
              <i className="fa fa-video me-2" />
              Video/Broadcast
            </label>
            <input
              id="distributionEvent"
              type="radio"
              name="distribution"
              className="btn-check"
              onChange={handleDistributionChange('event')}
              checked={distribution === 'event'}
            />
            <label
              htmlFor="distributionEvent"
              className={distribution === 'event' ? 'btn btn-sm btn-primary' : 'btn btn-sm new-request-option-btn'}
            >
              <i className="fa fa-calendar me-2" />
              Event
            </label>
            <input
              id="distributionOther"
              type="radio"
              name="distribution"
              className="btn-check"
              onChange={handleDistributionChange('other')}
              checked={distribution === 'other'}
            />
            <label
              htmlFor="distributionOther"
              className={distribution === 'other' ? 'btn btn-sm btn-primary' : 'btn btn-sm new-request-option-btn'}
            >
              Other
            </label>
          </div>
        </div>

        {showSpeakerCheckbox && (
          <div className="mb-4">
            <label htmlFor="speakerRequest" className="d-flex align-self-start">
              <input
                type="checkbox"
                id="speakerRequest"
                className="form-check-input me-2"
                checked={speakerRequest}
                onChange={() => toggleParam('speakerRequest')}
              />
              <span>
                This is a speaker request
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Connect with a panelist, moderator, or keynote</Tooltip>}
                >
                  <i className="ms-1 text-neutral-40 fa fa-info-circle" />
                </OverlayTrigger>
              </span>
            </label>
          </div>
        )}

        <div className={classNames({ 'd-none': !showEventType, 'mb-4': showEventType })}>
          <p className="mb-2">
            <b>Event Type</b>
          </p>
          <div
            className={classNames('d-flex flex-shrink-0 flex-wrap gap-2', {
              'is-invalid': !!validationErrors.eventType,
            })}
          >
            <input
              id="eventTypeWebinar"
              type="radio"
              name="distribution"
              className="btn-check"
              onChange={handleEventTypeChange('webinar')}
              checked={eventType === 'webinar'}
            />
            <label
              htmlFor="eventTypeWebinar"
              className={classNames('btn btn-sm', {
                'btn-primary': eventType === 'webinar',
                'new-request-option-btn': eventType !== 'webinar' && !validationErrors.eventType,
                'btn-outline-danger': !!validationErrors.eventType,
              })}
            >
              <i className="fa fa-video me-2" />
              Webinar
            </label>

            <input
              id="eventTypeLecture"
              type="radio"
              name="distribution"
              className="btn-check"
              onChange={handleEventTypeChange('lecture')}
              checked={eventType === 'lecture'}
            />
            <label
              htmlFor="eventTypeLecture"
              className={classNames('btn btn-sm', {
                'btn-primary': eventType === 'lecture',
                'new-request-option-btn': eventType !== 'lecture' && !validationErrors.eventType,
                'btn-outline-danger': !!validationErrors.eventType,
              })}
            >
              <i className="fa fa-chalkboard-user me-2" />
              Lecture
            </label>
            <input
              id="eventTypeConference"
              type="radio"
              name="distribution"
              className="btn-check"
              onChange={handleEventTypeChange('conference')}
              checked={eventType === 'conference'}
            />
            <label
              htmlFor="eventTypeConference"
              className={classNames('btn btn-sm', {
                'btn-primary': eventType === 'conference',
                'new-request-option-btn': eventType !== 'conference' && !validationErrors.eventType,
                'btn-outline-danger': !!validationErrors.eventType,
              })}
            >
              <i className="fa fa-screen-users me-2" />
              Conference
            </label>
          </div>
          {validationErrors.eventType && <div className="invalid-feedback d-block">{validationErrors.eventType}</div>}
        </div>
        <div className="d-flex flex-column">
          <p className="mb-2" style={{ fontSize: '16px' }}>
            <b>Additional Options</b> <i className="text-secondary">(optional)</i>
          </p>
          <label htmlFor="distributeExternally" className="mb-2 d-flex align-self-start">
            <input
              type="checkbox"
              id="distributeExternally"
              className="form-check-input me-2"
              value="distributeExternally"
              checked={!distributeExternally}
              onChange={() => toggleParam('distributeExternally')}
            />
            <span>
              Do not promote this request on Qwoted&apos;s social media
              <OverlayTrigger
                placement="top"
                overlay={(
                  <Tooltip>
                    Boost your story&apos;s reach! We can share an anonymized version of your request on our social
                    feeds, helping more experts discover and engage with your idea. Prefer to keep it off social? Simply
                    check this box.
                  </Tooltip>
                )}
              >
                <i className="ms-1 text-neutral-40 fa fa-info-circle" />
              </OverlayTrigger>
            </span>
          </label>
          <label htmlFor="paid" className="mb-2 d-flex align-self-start">
            <input
              type="checkbox"
              id="paid"
              className="form-check-input me-2"
              value="paid"
              checked={paid}
              onChange={useUpdateParamWithEvent('paid')}
            />
            <span>
              This is a fee-based opportunity
              <OverlayTrigger
                placement="top"
                overlay={(
                  <Tooltip>
                    Sometimes called pay-to-play, fee-based opportunities are paid placements used to gain exposure, or
                    access media platforms—often for marketing, brand visibility, or thought leadership
                  </Tooltip>
                )}
              >
                <i className="ms-1 text-neutral-40 fa fa-info-circle" />
              </OverlayTrigger>
            </span>
          </label>
          <label htmlFor="backlinkOpportunity" className="d-flex align-self-start">
            <input
              type="checkbox"
              id="backlinkOpportunity"
              className="form-check-input me-2"
              value="backlinkOpportunity"
              checked={backlinkOpportunity}
              onChange={useUpdateParamWithEvent('backlinkOpportunity')}
            />
            <span>
              This is a backlink opportunity
              <OverlayTrigger placement="top" overlay={<Tooltip>An opportunity to boost SEO and traffic</Tooltip>}>
                <i className="ms-1 text-neutral-40 fa fa-info-circle" />
              </OverlayTrigger>
            </span>
          </label>
        </div>
      </Panel>
    </div>
  );
}

MediaAndDistribution.propTypes = {
  backlinkOpportunity: PropTypes.bool,
  distribution: PropTypes.string,
  distributeExternally: PropTypes.bool,
  eventType: PropTypes.string,
  lookingFor: PropTypes.string,
  paid: PropTypes.bool,
  speakerRequest: PropTypes.bool,
  mediaOutletId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  toggleParam: PropTypes.func.isRequired,
  updateParam: PropTypes.func.isRequired,
  useUpdateParamWithEvent: PropTypes.func.isRequired,
  validationErrors: PropTypes.shape({
    mediaOutletId: PropTypes.string,
    eventType: PropTypes.string,
  }),
};

MediaAndDistribution.defaultProps = {
  backlinkOpportunity: false,
  distribution: null,
  distributeExternally: true,
  eventType: null,
  lookingFor: null,
  paid: false,
  speakerRequest: false,
  mediaOutletId: '',
  validationErrors: {},
};

export default MediaAndDistribution;
